<template>
  <v-app-bar color="secondary" density="compact" :flat="true">
    <v-toolbar-title class="text-button">{{ icdPresentationString }}: {{ icdDescription }} - {{ selected_menu_item.title }}</v-toolbar-title>
    <v-btn variant="text" icon="mdi-filter" @click.stop="filter_drawer = !filter_drawer"></v-btn>
    <v-btn variant="text" icon="mdi-information" @click.stop="information_drawer = !information_drawer"></v-btn>

    <template v-slot:extension>
      <v-tabs v-model="selected_menu_item" align-tabs="title" show-arrows>
        <v-tab v-for="menu_item in menu_items" :key="menu_item.dashboard" :value="menu_item"
               :disabled="menu_item.accessRight && !isAuthorized(menu_item.accessRight)">
          {{ menu_item.title }}
        </v-tab>
      </v-tabs>
    </template>
  </v-app-bar>

  <v-window v-model="selected_menu_item" direction="horizontal">
    <v-window-item :key="dashboard_1.dashboard" :value="dashboard_1" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_1.visible = event.value">
      <SocialstyrelsenGenericDemographicBaseline :icd_or_atc="this.selected_icd.map(i => i.value)"
                                                 :dataset="this.selected_dataset"
                                                 :measurement-type="this.selected_measurement_type"
                                                 :all-ages-at-diagnosis="this.select_age_at_diagnosis_items"
                                                 :age-at-diagnosis="this.selected_age_at_diagnosis"
                                                 :diagnosis-date="this.selected_diagnosis_date"
                                                 :sex="this.selected_sex"
                                                 :region="this.selected_region"
                                                 :visible="this.dashboard_1.visible"
                                                 :per-x="this.selected_per_100k ? 100_000 : undefined"
                                                 v-on:regionSelected="regionSelected($event)"
                                                 v-on:sexSelected="sexSelected($event)"
                                                 v-on:ageGroupSelected="ageGroupSelected($event)"
                                                 v-on:totalNumberOfPatientsPerYearSelected="totalNumberOfPatientsPerYearSelected($event)">
      </SocialstyrelsenGenericDemographicBaseline>
    </v-window-item>
    <v-window-item :key="dashboard_2.dashboard" :value="dashboard_2" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_2.visible = event.value">
      <SocialstyrelsenGenericTrends :title="'Longitudinal trends - ' + this.selected_dataset.title"
                                    trends-url="/socialstyrelsen-generic/longitudinal-trends"
                                    trends-horizontal-axis-label="Year"
                                    :year-group-included="false"
                                    :region-group-included="true"
                                    :icd_or_atc="this.selected_icd.map(i => i.value)"
                                    :dataset="this.selected_dataset"
                                    :measurement-type="this.selected_measurement_type"
                                    :age-at-diagnosis="this.selected_age_at_diagnosis"
                                    :diagnosis-date="this.selected_diagnosis_date"
                                    :sex="this.selected_sex"
                                    :region="this.selected_region"
                                    :datasets="computeTrendsDatasets()"
                                    :visible="this.dashboard_2.visible"
                                    :per-x="this.selected_per_100k ? 100_000 : undefined">
      </SocialstyrelsenGenericTrends>
    </v-window-item>
    <v-window-item :key="dashboard_3.dashboard" :value="dashboard_3" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_3.visible = event.value">
      <SocialstyrelsenGenericTrends :title="'Regional trends - ' + this.selected_dataset.title"
                                    trends-url="/socialstyrelsen-generic/regional-trends"
                                    trends-horizontal-axis-label="Region"
                                    :year-group-included="true"
                                    :region-group-included="false"
                                    :dataset="this.selected_dataset"
                                    :measurement-type="this.selected_measurement_type"
                                    :icd_or_atc="this.selected_icd.map(i => i.value)"
                                    :age-at-diagnosis="this.selected_age_at_diagnosis"
                                    :diagnosis-date="this.selected_diagnosis_date"
                                    :sex="this.selected_sex"
                                    :region="this.selected_region"
                                    :datasets="computeTrendsDatasets()"
                                    :visible="this.dashboard_3.visible"
                                    :per-x="this.selected_per_100k ? 100_000 : undefined">
      </SocialstyrelsenGenericTrends>
    </v-window-item>
  </v-window>

  <v-navigation-drawer v-model="information_drawer" v-bind:width="500" location="right" temporary>
    <v-list density="compact" nav>
      <v-list-item prepend-icon="mdi-information" title="Information about the data set" nav></v-list-item>
    </v-list>
    <v-divider></v-divider>

    <v-list density="compact" nav>
      <v-divider v-if="socialstyrelsen_generic_cleaning_report.length > 0" :vertical="true"></v-divider>
      <v-list-item v-if="socialstyrelsen_generic_cleaning_report.length > 0">
        <strong>National board of health data reports</strong>
      </v-list-item>
      <v-list-item v-for="socialstyrelsen_generic_cleaning_report_line in socialstyrelsen_generic_cleaning_report"
                   :key="socialstyrelsen_generic_cleaning_report_line"
                   :value="socialstyrelsen_generic_cleaning_report_line" prepend-icon="mdi:mdi-minus">
        {{ socialstyrelsen_generic_cleaning_report_line }}
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500">
    <v-card class="mx-auto epix-filter" elevation="0" ref="filters">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <v-combobox v-model="selected_dataset" :items="selected_dataset_items"
                          label="Patient selection" :rules="[rules.required]" @click="$refs.filter_form.validate()">
              </v-combobox>
              <v-combobox v-model="selected_measurement_type" :items="selected_measurement_type_items"
                          label="Measurement type" :rules="[rules.required]" @click="$refs.filter_form.validate()">
              </v-combobox>
              <v-checkbox v-model="selected_per_100k" label="Show values per 100 000 inhabitants"
                          :disabled="selected_measurement_type.value === 'average_care_time'"
                          hide-details></v-checkbox>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_age_at_diagnosis" :items="select_age_at_diagnosis_items"
                              label="Age" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              hint="The patient's age at the end of year or at time of death."
                              multiple persistent-hint></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneAgeGroups">All/None</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="9">
                  <v-combobox class="mb-5" v-model="selected_diagnosis_date" :items="select_diagnosis_date_items"
                              label="Year" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              hint="Year of visit, discharge, or death."
                              multiple persistent-hint></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneYears">All/None</v-btn>
                </v-col>
              </v-row>
              <v-combobox v-model="selected_sex" :items="select_sex_items"
                          label="Sex" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                          multiple></v-combobox>
              <v-row>
                <v-col cols="9">
                  <v-combobox class="mb-5" v-model="selected_region" :items="select_region_items"
                              label="Region" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              hint="The region is based on the patient's registration in the population register on
                              the last of December each year, except for preliminary data or death when the patient's
                              region is from the time of visit/discharge/death."
                              multiple persistent-hint></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneRegions">All/None</v-btn>
                </v-col>
              </v-row>
              <v-combobox v-model="selected_icd" :items="select_icd_items"
                          label="ICD code" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                          multiple></v-combobox>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['filters'].$el" :document-title="icd + '-' + 'filters'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import SocialstyrelsenGenericDemographicBaseline from '@/components/SocialstyrelsenGenericDemographicBaseline.vue'
import SocialstyrelsenGenericTrends from '@/components/SocialstyrelsenGenericTrends.vue'
import {defineComponent} from "vue";
import axios from "axios";
import store from '@/store';
import { VueToPrint } from "vue-to-print";

const DASHBOARD_1 = {
  title: 'Epidemiology and baseline characteristics',
  dashboard: 'Dashboard1',
  visible: true
};


const DASHBOARD_2 = {
  title: 'Longitudinal trends',
  dashboard: 'Dashboard2',
  visible: false
};

const DASHBOARD_3 = {
  title: 'Regional trends',
  dashboard: 'Dashboard3',
  visible: false
};

const SPECIALIST_CARE = {
  value: 'SPECIALIST_CARE',
  title: 'Specialist care (all)'
};

const IN_PATIENT_CARE = {
  value: 'IN_PATIENT_CARE',
  title: 'Inpatient care'
};

const OUT_PATIENT_CARE = {
  value: 'OUT_PATIENT_CARE',
  title: 'Outpatient care'
};

const CAUSE_OF_DEATH = {
  value: 'CAUSE_OF_DEATH',
  title: 'Cause of death'
};

function generateIcdTitle(icd) {
  if (store.getters.stateSocialstyrelsenGenericIcdMap && store.getters.stateSocialstyrelsenGenericIcdMap.has(icd)) {
    return icd + ' (' + store.getters.stateSocialstyrelsenGenericIcdMap.get(icd) + ')'
  }
  return icd
}

export default defineComponent({
  name: 'SocialstyrelsenGenericView',
  props: {
    icd: String,
    icdPresentationString: String,
    icdDescription: String
  },
  components: {
    SocialstyrelsenGenericDemographicBaseline,
    SocialstyrelsenGenericTrends,
    VueToPrint
  },
  data: context => ({
    dashboard_1: DASHBOARD_1,
    dashboard_2: DASHBOARD_2,
    dashboard_3: DASHBOARD_3,
    selected_menu_item: DASHBOARD_1,
    selected_dataset: SPECIALIST_CARE,
    selected_dataset_items: [SPECIALIST_CARE, IN_PATIENT_CARE, OUT_PATIENT_CARE, CAUSE_OF_DEATH],
    selected_measurement_type: {
      value: 'number_of_patients',
      title: 'Number of patients'
    },
    selected_measurement_type_items: [{
      value: 'number_of_patients',
      title: 'Number of patients'
    }],
    select_age_at_diagnosis_items: store.getters.stateSocialstyrelsenGenericAgesAtDiagnosis,
    selected_age_at_diagnosis: store.getters.stateSocialstyrelsenGenericAgesAtDiagnosis,
    select_diagnosis_date_items: store.getters.stateSocialstyrelsenGenericDiagnosisYears,
    selected_diagnosis_date: store.getters.stateSocialstyrelsenGenericDiagnosisYears,
    select_sex_items: store.getters.stateSocialstyrelsenGenericSexes,
    selected_sex: store.getters.stateSocialstyrelsenGenericSexes,
    select_region_items: store.getters.stateSocialstyrelsenGenericRegions,
    selected_region: store.getters.stateSocialstyrelsenGenericRegions,
    select_icd_items: context.icd.split('_').map(icd => ({value: icd, title: generateIcdTitle(icd)})),
    selected_icd: context.icd.split('_').map(icd => ({value: icd, title: generateIcdTitle(icd)})),
    selected_per_100k: false,
    socialstyrelsen_generic_cleaning_report: [],
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      }
    },
    information_drawer: false,
    filter_drawer: false,
    menu_items: [
      DASHBOARD_1,
      DASHBOARD_2,
      DASHBOARD_3
    ]
  }),
  methods: {
    isAuthorized(accessRight) {
      return this.$store.getters.stateUser
          && this.$store.getters.stateUser.access_rights
          && this.$store.getters.stateUser.access_rights.includes(accessRight)
    },
    regionSelected(region) {
      if ((this.selected_region.length === 1) && (this.selected_region[0] === region)) {
        // Region deselection
        this.selected_region = [...this.select_region_items];
      } else {
        this.selected_region = [region];
      }
    },
    sexSelected(sex) {
      if ((this.selected_sex.length === 1) && (this.selected_sex[0] === sex)) {
        // Sex deselection
        this.selected_sex = [...this.select_sex_items];
      } else {
        this.selected_sex = [sex];
      }
    },
    ageGroupSelected(ageGroup) {
      if ((this.selected_age_at_diagnosis.length === 1) && (this.selected_age_at_diagnosis[0] === ageGroup)) {
        // Age group deselection
        this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items];
      } else {
        this.selected_age_at_diagnosis = [ageGroup];
      }
    },
    totalNumberOfPatientsPerYearSelected(year) {
      if ((this.selected_diagnosis_date.length === 1) && (this.selected_diagnosis_date[0] === year)) {
        // Year deselection
        this.selected_diagnosis_date = [...this.select_diagnosis_date_items];
      } else {
        this.selected_diagnosis_date = [year];
      }
    },
    toggleAllNoneAgeGroups() {
      if (this.selected_age_at_diagnosis.length === this.select_age_at_diagnosis_items.length) {
        this.selected_age_at_diagnosis = [];
      } else {
        this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items]
      }
    },
    toggleAllNoneYears() {
      if (this.selected_diagnosis_date.length === this.select_diagnosis_date_items.length) {
        this.selected_diagnosis_date = [];
      } else {
        this.selected_diagnosis_date = [...this.select_diagnosis_date_items]
      }
    },
    toggleAllNoneRegions() {
      if (this.selected_region.length === this.select_region_items.length) {
        this.selected_region = [];
      } else {
        this.selected_region = [...this.select_region_items]
      }
    },
    computeTrendsDatasets() {
      if (this.selected_dataset.value === CAUSE_OF_DEATH.value) {
        return [CAUSE_OF_DEATH]
      } else {
        return [SPECIALIST_CARE, IN_PATIENT_CARE, OUT_PATIENT_CARE]
      }
    },
    async loadReport() {
      let data = axios.get('/socialstyrelsen-generic/report',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.selected_icd.map(i => i.value).join(','),
              dataset: this.selected_dataset.value,
            }
          });
      data.then((response) => {
        this.socialstyrelsen_generic_cleaning_report = response.data;
      });
    },
    async loadDynamicData() {
      await Promise.all([
        this.loadReport()
      ]).then(() => {
        console.log('Dynamic data loaded: SocialstyrelsenGenericView')
      })
    }
  },
  watch: {
    icd(newIcd) {
      this.select_icd_items = newIcd.split('_').map(icd => ({value: icd, title: generateIcdTitle(icd)}))
      this.selected_icd = newIcd.split('_').map(icd => ({value: icd, title: generateIcdTitle(icd)}))
    },
    selected_icd() {
      this.loadDynamicData()
    },
    selected_dataset(newDataset) {
      if (newDataset.value === SPECIALIST_CARE.value) {
        this.selected_measurement_type_items = [{
          value: 'number_of_patients',
          title: 'Number of patients'
        }]
        this.select_age_at_diagnosis_items=[...store.getters.stateSocialstyrelsenGenericAgesAtDiagnosis]
      } else if (newDataset.value === IN_PATIENT_CARE.value) {
        this.selected_measurement_type_items = [
            {
              value: 'number_of_patients',
              title: 'Number of patients'
            }, {
            value: 'care_time_in_days',
            title: 'Care time in days'
          }, {
            value: 'care_events',
            title: 'Care events'
          },
          {
            value: 'average_care_time',
            title: 'Average care time (days per care event)'
          }
        ]
        this.select_age_at_diagnosis_items=[...store.getters.stateSocialstyrelsenGenericAgesAtDiagnosis]
      } else if (newDataset.value === OUT_PATIENT_CARE.value) {
        this.selected_measurement_type_items = [{
          value: 'number_of_patients',
          title: 'Number of patients'
        }, {
          value: 'number_of_visits',
          title: 'Number of visits'
        }]
        this.select_age_at_diagnosis_items=[...store.getters.stateSocialstyrelsenGenericAgesAtDiagnosis]
      } else {
        this.selected_measurement_type_items = [{
          value: 'number_of_dead',
          title: 'Number of dead'
        }]
        this.select_age_at_diagnosis_items=[...store.getters.stateSocialstyrelsenGenericAgesAtCauseOfDeathDiagnosis]
      }
      this.selected_measurement_type = this.selected_measurement_type_items[0]
      this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items]
      this.loadDynamicData()
    },
    selected_measurement_type(newMeasurementType) {
      if (newMeasurementType.value === 'average_care_time') {
        this.selected_per_100k = false
      }
    }
  },
  async mounted() {
    await this.loadDynamicData()
  }
})
</script>
